import cn from 'classnames';
import { isEqual } from 'lodash';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { startOfHour, addMinutes } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, IconButton } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import CustomQuestionModal from 'components/CustomQuestionModal';
import Header from 'components/Header';
import TouchpointQuestions from 'components/TouchpoinQuestions';
import { Row } from 'components/layout';
import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { QuestionState } from 'lib/models/answers';
import {
  JobAttributes,
  JobPostingState,
  JobRequestStep3,
} from 'lib/models/job';
import { TouchpointState } from 'lib/models/opportunity';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import { isValidDate } from 'utils/time';
import {
  dateIsValid,
  formatDateToRetrieve,
  formatDateToSave,
} from 'utils/format';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import DragAndDrop from '../../DragAndDrop';
import { FieldStateOption } from '../Job.component';
import styles from '../Step.module.scss';

const DateTimePicker = dynamic(() => import('components/DateTimePicker'), {
  ssr: false,
});

export const QuestionOptions = [
  {
    id: '1',
    label: QuestionState.OFF,
    value: QuestionState.OFF,
  },
  {
    id: '2',
    label: QuestionState.OPTIONAL,
    value: QuestionState.OPTIONAL,
  },
  {
    id: '3',
    label: QuestionState.REQUIRED,
    value: QuestionState.REQUIRED,
    groupEnd: true,
  },
  {
    id: '4',
    label: QuestionState.EDIT,
    value: QuestionState.EDIT,
    isEdit: true,
  },
  {
    id: '5',
    label: QuestionState.DELETE,
    value: QuestionState.DELETE,
    isDelete: true,
  },
];

export interface Step3Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: JobAttributes | null;
  onSave: ({
    data,
    isValueEdited,
    goToStep,
    isPreviewClicked,
  }: {
    data: JobRequestStep3;
    isValueEdited: boolean;
    goToStep?: number;
    isPreviewClicked?: boolean;
  }) => void;
  isDraftLoading: boolean;
  isLoading: boolean;
  questionTypes: Record<string, { label: string; choice: number }>;
}

export const Step3: React.FC<Step3Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    onSave,
    isDraftLoading,
    isLoading,
    questionTypes,
  }: Step3Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );
    const router = useRouter();
    const [t] = useTranslation('opportunity.posting');
    const { onboarding } = router.query;
    const { mutateCompanyCampaigns } = useCampaigns();

    const [state, setState] = useState<string>();
    const [deadline, setDeadline] = useState<Date | null>(null);
    const [showPicker, setShowPicker] = useState(false);
    const [applicationProcess, setApplicationProcess] = useState<
      Array<FieldStateOption>
    >([
      {
        id: '1',
        value: '',
        isHovered: false,
        toBeFocussed: true,
      },
    ]);

    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<JobRequestStep3>();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState('');
    const [selectedQuestion, setSelectedQuestion] = useState<{
      id?: string;
      title?: string;
      state?: QuestionState;
      isNew?: boolean;
      options?: Array<string>;
      question_type: string;
    } | null>(null);
    const [customQuestionsList, setCustomQuestionsList] = useState<
      Array<{
        id?: string;
        title: string;
        state: QuestionState;
        isNew?: boolean;
        options?: Array<string>;
        question_type: string;
      }>
    >([]);
    const [isCVRequiredState, setCVRequiredState] = useState<QuestionState>(
      QuestionState.OFF
    );
    const [isLinkedInProfileRequiredState, setLinkedInProfileRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [isOpenCustomQuestionModal, setIsOpenCustomQuestionModal] =
      useState(false);
    const [removedCustomQuestionIds, setRemovedCustomQuestionIds] = useState<
      Array<string>
    >([]);

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
      if (!isOpenCustomQuestionModal) {
        setSelectedQuestionId('');
        setSelectedQuestion(null);
      }
    }, [isOpenCustomQuestionModal]);

    useEffect(() => {
      if (selectedQuestionId) {
        const question = customQuestionsList.find(
          (ques) => ques.id == selectedQuestionId
        );
        setSelectedQuestion(question || null);
        setIsOpenCustomQuestionModal(true);
      }
    }, [selectedQuestionId]);

    useEffect(() => {
      if (touchpoint) {
        const {
          application_deadline,
          application_process,
          state,
          linkedin_profile_required_state,
          cv_required_state,
          questions: questionsResponse,
        } = touchpoint;

        const { start_date, end_date } = parseResponse(
          touchpoint.touchpointable
        );

        if (start_date) {
          setStartDate(formatDateToRetrieve(start_date));
        }

        if (end_date) {
          setEndDate(formatDateToRetrieve(end_date));
        }
        if (application_deadline) {
          setDeadline(formatDateToRetrieve(application_deadline));
          setShowPicker(true);
        }

        if (application_process.length > 0) {
          setApplicationProcess(
            application_process.map((item) => ({
              id: uuidv4(),
              value: item,
              isHovered: false,
            }))
          );
        }
        setCVRequiredState(cv_required_state);
        setLinkedInProfileRequiredState(linkedin_profile_required_state);

        const questions = parseArrayResponse(questionsResponse);
        setCustomQuestionsList(questions);

        setAvailableTouchpointData({
          application_deadline: application_deadline
            ? formatDateToSave(formatDateToRetrieve(application_deadline))
            : null,
          application_process: application_process.filter(
            (item) => item !== ''
          ),
          state,
          cv_required_state,
          linkedin_profile_required_state,
          questions_attributes: customQuestionsList,
          removedCustomQuestionIds: [],
        });
        setState(state);
      }
    }, [touchpoint]);

    const areValuesEdited = (submitType?: TouchpointState) => {
      if (touchpoint) {
        const selectedAttributes = {
          application_deadline: deadline ? formatDateToSave(deadline) : null,
          application_process: applicationProcess
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          state:
            touchpoint.state === JobPostingState.PUBLISHED
              ? JobPostingState.PUBLISHED
              : submitType === TouchpointState.SAVE ||
                submitType === TouchpointState.DRAFT
              ? JobPostingState.DRAFT
              : JobPostingState.SUBMIT_FOR_APPROVAL,
          questions_attributes: customQuestionsList,
          removedCustomQuestionIds,
          cv_required_state: isCVRequiredState,
          linkedin_profile_required_state: isLinkedInProfileRequiredState,
        };

        return !isEqual(selectedAttributes, availableTouchpointData);
      } else {
        return true;
      }
    };

    const onClose = async () => {
      if (areValuesEdited(TouchpointState.SAVE)) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: false, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
            return;
          }
          router.push(PAGE_ROUTES.HIRING);
        }
      } else {
        if (onboarding) {
          router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
          return;
        }
        mutateCompanyCampaigns();
        router.push(PAGE_ROUTES.HIRING);
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;
      setIsSubmitted(true);
      if (isInvalid) return;
      setIsSubmitted(false);
      onSubmit({ checkRequired: false, goToStep });
    };

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep,
      isPreviewClicked,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
      isPreviewClicked?: boolean;
    }) => {
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited(submitType);

        const data = {
          application_deadline: deadline ? formatDateToSave(deadline) : null,
          application_process: applicationProcess
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          state: goToStep
            ? touchpoint?.state || JobPostingState.DRAFT
            : touchpoint?.state === JobPostingState.PUBLISHED
            ? JobPostingState.PUBLISHED
            : submitType === TouchpointState.SAVE ||
              submitType === TouchpointState.DRAFT
            ? JobPostingState.DRAFT
            : JobPostingState.SUBMIT_FOR_APPROVAL,
          submitType,
          questions_attributes: customQuestionsList,
          removedCustomQuestionIds,
          cv_required_state: isCVRequiredState,
          linkedin_profile_required_state: isLinkedInProfileRequiredState,
        };

        onSave({ data, isValueEdited, goToStep, isPreviewClicked });
      };
      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
      }
      onSaveRequest();
    };

    const onPreviewClick = () => {
      if (touchpoint) {
        onSubmit({
          checkRequired: false,
          submitType: TouchpointState.SAVE,
          isPreviewClicked: true,
        });
        const { slug, touchpointable_type } = touchpoint;
        const publicPageUrl = `${
          window.location.origin
        }${getOpportunityPublicPagePathname(touchpointable_type, slug || '')}`;
        window.open(publicPageUrl, '_blank');
      }
    };

    const defaultQuestionOptions = useMemo(() => {
      return QuestionOptions.filter((ques) => !ques?.isDelete && !ques.isEdit);
    }, [QuestionOptions]);

    const handleAddQuestion = (
      event: React.MouseEvent<HTMLButtonElement>,
      {
        title,
        isRequired,
        optionsList,
        questionType,
        isEdit,
      }: {
        optionsList: Array<string>;
        isRequired: boolean;
        questionType: string;
        title: string;
        isEdit: boolean;
      }
    ) => {
      event.stopPropagation();
      event.preventDefault();
      const data = {
        ...selectedQuestion,
        title,
        state: isRequired ? QuestionState?.REQUIRED : QuestionState.OPTIONAL,
        question_type: questionType,
        ...((questionType && questionType === 'single_choice') ||
        (questionType === 'multiple_choice' && optionsList?.length)
          ? { options: optionsList }
          : {}),
      };
      if (isEdit) {
        setCustomQuestionsList((prevQues) =>
          prevQues.map((q) => {
            if (q.id === selectedQuestion?.id) {
              return { ...data };
            } else {
              return q;
            }
          })
        );
      } else {
        setCustomQuestionsList([
          ...customQuestionsList,
          {
            id: uuidv4(),
            title,
            state: isRequired
              ? QuestionState?.REQUIRED
              : QuestionState.OPTIONAL,
            isNew: true,
            question_type: questionType,
            ...((questionType && questionType === 'single_choice') ||
            (questionType === 'multiple_choice' && optionsList?.length)
              ? { options: optionsList }
              : {}),
          },
        ]);
      }
      setIsOpenCustomQuestionModal(false);
      setSelectedQuestion(null);
    };
    const onChangeCustomQuesState = (
      questionId: string,
      state: QuestionState
    ) => {
      setSelectedQuestionId('');
      if (state === QuestionState.DELETE) {
        const deletedQues = customQuestionsList.find(
          (q) => q.id === questionId
        );
        if (deletedQues && !deletedQues.isNew) {
          setRemovedCustomQuestionIds((questionIds) => [
            ...questionIds,
            questionId,
          ]);
        }
        setCustomQuestionsList((list) =>
          list.filter((ques) => ques.id !== questionId)
        );
      } else if (state === QuestionState.EDIT) {
        setSelectedQuestionId(questionId);
      } else {
        setCustomQuestionsList((list) =>
          list.map((ques) => {
            if (ques.id === questionId) {
              return {
                ...ques,
                state,
              };
            } else return ques;
          })
        );
      }
    };

    const primaryButtonText = useMemo(() => {
      if (
        state === TouchpointState.READY_FOR_APPROVAL ||
        state === TouchpointState.PUBLISHED
      )
        return t('job-posting.save-changes');
      if (state === TouchpointState.APPROVED) return t('job-posting.publish');
      if (state === TouchpointState.DRAFT)
        return t('job-posting.submit-for-approval');
      else return t('job-posting.submit-for-approval');
    }, [state]);

    const applicationProcessPlaceholder = [
      t('job-posting.apply-on-huzzle'),
      t('job-posting.video-interview'),
      t('job-posting.take-home'),
      t('job-posting.final-interview'),
      t('job-posting.add-application-step'),
    ];
    const isPreviousDate =
      isValidDate(deadline) && new Date(deadline || '') < new Date();

    const isInvalid =
      (deadline ? !dateIsValid(deadline) : false) ||
      (showPicker && !deadline) ||
      isPreviousDate;

    const currentTime = startOfHour(new Date()); // Get the current hour, rounded down
    const roundedCurrentTime = addMinutes(
      currentTime,
      Math.round(currentTime.getMinutes() / 30) * 30
    );

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onSubmit({ checkRequired: true })}
          onNextLoading={isLoading}
          onPreviewClick={onPreviewClick}
          className={styles.header}
          primaryButtonText={primaryButtonText}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('job-posting.applications-deadline')}{' '}
                  <span>({t('job-posting.optional')})</span>
                </p>
                <p className={styles.desc}>
                  {t('job-posting.application-deadline-info')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <article className={styles.main}>
                <Row
                  className={cn(styles.datepickerRow, styles.inputContainer, {
                    [styles.hide]: !showPicker,
                    [styles.show]: showPicker,
                  })}
                >
                  <DateTimePicker
                    label="Registration deadline date and time"
                    min={roundedCurrentTime}
                    max={endDate || startDate || undefined}
                    value={deadline as Date}
                    onChange={(value) => setDeadline(new Date(value))}
                    isValid={isSubmitted && !isInvalid}
                    helperText={
                      isSubmitted && isInvalid
                        ? isPreviousDate
                          ? 'Select valid date'
                          : 'Required'
                        : ''
                    }
                    endDateFormatter="yyyy-MM-dd'T'24:00"
                  />
                  <IconButton
                    iconName="close"
                    size="xsmall"
                    onClick={() => {
                      setDeadline(null);
                      setShowPicker(false);
                    }}
                    className={cn(styles.remove, styles.removeDeadline)}
                  />
                </Row>

                <div
                  className={cn(styles.deadlineContainer, {
                    [styles.hide]: showPicker,
                    [styles.show]: !showPicker,
                  })}
                >
                  <p
                    onClick={() => {
                      setShowPicker(true);
                    }}
                    className={cn(styles.deadlineEmpty)}
                  >
                    Rolling basis
                    <Icon iconName="chevron-down" />
                  </p>
                </div>
              </article>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('job-posting.application-process')}{' '}
                  <span>({t('job-posting.optional')})</span>
                </p>
                <p className={styles.desc}>
                  {t('job-posting.application-process-info')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <article className={styles.article}>
                <DragAndDrop
                  placeholder={applicationProcessPlaceholder}
                  fieldState={applicationProcess}
                  setFieldState={setApplicationProcess}
                />
                <div
                  className={cn({
                    [styles.gap]: applicationProcess.length > 0,
                  })}
                ></div>
                <ButtonV3
                  size="small"
                  color="secondary"
                  startIcon="plus"
                  onClick={() =>
                    setApplicationProcess([
                      ...applicationProcess,
                      {
                        id: uuidv4(),
                        value: '',
                        isHovered: false,
                        toBeFocussed: true,
                      },
                    ])
                  }
                  className={styles.articleButton}
                >
                  {t('job-posting.add-step')}
                </ButtonV3>
              </article>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('job-posting.application-question')}
                  <span>({t('job-posting.optional')})</span>
                </p>
                <p className={styles.desc}>
                  <Trans
                    t={t}
                    i18nKey="job-posting.registration-questions-desc"
                  >
                    We collect <span>Name</span>, <span>Email</span> and{' '}
                    <span>Study details</span> by default. Ask only for
                    information you must know, asking too many and difficult
                    questions may result in fewer applications. 😱
                  </Trans>
                </p>
              </main>
              <div className={styles.separator}></div>
              <article className={styles.StepQuestionsArticle}>
                <TouchpointQuestions
                  isCVRequiredState={isCVRequiredState}
                  setCVRequiredState={setCVRequiredState}
                  isLinkedInProfileRequiredState={
                    isLinkedInProfileRequiredState
                  }
                  setLinkedInProfileRequiredState={
                    setLinkedInProfileRequiredState
                  }
                  customQuestionsList={customQuestionsList}
                  QuestionOptions={QuestionOptions}
                  onChangeCustomQuesState={onChangeCustomQuesState}
                  defaultQuestionOptions={defaultQuestionOptions}
                  dropDownClassName={styles.StepQuestionsArticleDropdownQues}
                  questionListClassName={styles.questionList}
                  customQuesClassName={styles.customQuesRow}
                  isEventPostingFlow={false}
                  triggerButtonClassName={
                    styles.StepQuestionsArticleDropdownQuesButton
                  }
                  isDefaultTriggerClass={false}
                />
                <ButtonV3
                  size="small"
                  startIcon="plus"
                  variant="contained"
                  color="secondary"
                  type="button"
                  onClick={() => {
                    setSelectedQuestion(null);
                    setSelectedQuestionId('');
                    setIsOpenCustomQuestionModal(true);
                  }}
                  className={styles.questionBtn}
                >
                  {t('job-posting.add-custom-question')}
                </ButtonV3>
              </article>
            </section>
            <Row className={styles.buttonRow}>
              {state === TouchpointState.DRAFT ? (
                <ButtonV3
                  color="secondary"
                  onClick={() =>
                    onSubmit({
                      checkRequired: false,
                      submitType: TouchpointState.DRAFT,
                    })
                  }
                  disabled={isSubmitted && isInvalid}
                  isLoading={isDraftLoading}
                  className={styles.nextSecondary}
                >
                  {t('job-posting.save-as-draft')}
                </ButtonV3>
              ) : null}
              {primaryButtonText ? (
                <ButtonV3
                  type="submit"
                  onClick={() => onSubmit({ checkRequired: true })}
                  disabled={isSubmitted && isInvalid}
                  isLoading={isLoading}
                  className={styles.submit}
                >
                  {primaryButtonText}
                </ButtonV3>
              ) : null}
            </Row>
          </div>
        </div>
        {isOpenCustomQuestionModal ? (
          <CustomQuestionModal
            isOpen={isOpenCustomQuestionModal}
            onClose={() => setIsOpenCustomQuestionModal(false)}
            onAddQuestion={handleAddQuestion}
            questionTypes={questionTypes}
            selectedQuestion={selectedQuestion}
          />
        ) : null}
      </>
    );
  }
);

Step3.displayName = 'Step3';
